import { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Play, FileText, BookOpen, Video, Link, Search, Pencil, Trash2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIES = ['safety', 'equipment', 'construction', 'sales', 'customer_service', 'company_policy', 'software_training', 'general'];
const TYPES = { video: { icon: Video, color: 'bg-red-50 text-red-600' }, document: { icon: FileText, color: 'bg-blue-50 text-blue-600' }, link: { icon: Link, color: 'bg-violet-50 text-violet-600' }, course: { icon: BookOpen, color: 'bg-emerald-50 text-emerald-600' } };
const EMPTY = { title: '', description: '', category: 'general', type: 'video', url: '', thumbnail: '', duration: '', required_for: [], order: 10, is_published: true };

export default function LearningCenter() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [resources, setResources] = useState([]);
  const [catFilter, setCatFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [dialog, setDialog] = useState(false);
  const [editRes, setEditRes] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(async () => {
    const params = {};
    if (catFilter !== 'all') params.category = catFilter;
    const r = await api.get('/company/learning', { params });
    setResources(r.data);
  }, [catFilter]);
  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    try {
      if (editRes) { await api.put(`/company/learning/${editRes.id}`, form); toast.success('Updated'); }
      else { await api.post('/company/learning', form); toast.success('Resource added'); }
      setDialog(false); setEditRes(null); setForm(EMPTY); load();
    } catch { toast.error('Failed'); }
  };

  const filtered = resources.filter(r => !search || r.title.toLowerCase().includes(search.toLowerCase()) || r.description?.toLowerCase().includes(search.toLowerCase()));
  const grouped = {};
  filtered.forEach(r => { if (!grouped[r.category]) grouped[r.category] = []; grouped[r.category].push(r); });

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px] mx-auto" data-testid="learning-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-[#1e3a50] tracking-tight">Learning Center</h1>
          <p className="text-[#6b8a9e] text-sm font-medium mt-0.5">Training videos, resources & company docs</p>
        </div>
        {isAdmin && (
          <Button className="bg-[#4a9ac5] hover:bg-[#3d87b3] text-white" onClick={() => { setEditRes(null); setForm(EMPTY); setDialog(true); }} data-testid="add-resource-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Resource
          </Button>
        )}
      </div>

      <div className="flex gap-3 mb-6">
        <div className="relative flex-1 max-w-sm">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6b8a9e]" />
          <Input placeholder="Search training materials..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-10" data-testid="learning-search" />
        </div>
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger className="w-44 h-10"><SelectValue placeholder="All Categories" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {CATEGORIES.map(c => <SelectItem key={c} value={c} className="capitalize">{c.replace('_', ' ')}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {Object.keys(grouped).length === 0 ? (
        <div className="text-center py-16">
          <BookOpen className="w-14 h-14 text-gray-200 mx-auto mb-4" />
          <p className="text-lg font-bold text-gray-400">No training resources yet</p>
          {isAdmin && <p className="text-sm font-medium text-[#6b8a9e] mt-1">Add videos, documents, and links for your team</p>}
        </div>
      ) : (
        <div className="space-y-8">
          {Object.entries(grouped).map(([cat, items]) => (
            <div key={cat}>
              <h2 className="text-sm font-bold text-[#2a6496] uppercase tracking-wider mb-3 capitalize">{cat.replace('_', ' ')}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {items.map(res => {
                  const meta = TYPES[res.type] || TYPES.video;
                  const Icon = meta.icon;
                  return (
                    <Card key={res.id} className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.04)] hover:shadow-md transition-shadow group" data-testid={`resource-${res.id}`}>
                      <CardContent className="p-0">
                        {res.thumbnail ? (
                          <div className="relative h-36 bg-gray-100 rounded-t-lg overflow-hidden">
                            <img src={res.thumbnail} alt="" className="w-full h-full object-cover" />
                            <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                              <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center"><Play className="w-5 h-5 text-[#1e3a50] ml-0.5" /></div>
                            </div>
                          </div>
                        ) : (
                          <div className={`h-28 ${meta.color.split(' ')[0]} rounded-t-lg flex items-center justify-center`}>
                            <Icon className={`w-10 h-10 ${meta.color.split(' ')[1]} opacity-50`} />
                          </div>
                        )}
                        <div className="p-4">
                          <div className="flex items-start justify-between mb-1">
                            <p className="text-sm font-bold text-[#1e3a50] line-clamp-2">{res.title}</p>
                            {isAdmin && (
                              <div className="flex gap-0.5 ml-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button onClick={() => { setEditRes(res); setForm(res); setDialog(true); }}><Pencil className="w-3.5 h-3.5 text-gray-400" /></button>
                                <button onClick={async () => { await api.delete(`/company/learning/${res.id}`); toast.success('Deleted'); load(); }}><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
                              </div>
                            )}
                          </div>
                          {res.description && <p className="text-[11px] font-medium text-[#6b8a9e] line-clamp-2 mb-2">{res.description}</p>}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Badge className={`text-[9px] font-bold ${meta.color}`}>{res.type}</Badge>
                              {res.duration && <span className="text-[10px] font-bold text-[#6b8a9e]">{res.duration}</span>}
                            </div>
                            {res.url && (
                              <a href={res.url} target="_blank" rel="noopener noreferrer"
                                className="flex items-center gap-1 text-[10px] font-bold text-[#4a9ac5] hover:underline">
                                Open <ExternalLink className="w-2.5 h-2.5" />
                              </a>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="resource-dialog">
          <DialogHeader><DialogTitle>{editRes ? 'Edit Resource' : 'Add Training Resource'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs font-bold">Title *</Label><Input value={form.title} onChange={e => setForm(p => ({...p, title: e.target.value}))} placeholder="Safety Training — PPE Requirements" data-testid="res-title" /></div>
            <div><Label className="text-xs font-bold">Description</Label><Textarea value={form.description} onChange={e => setForm(p => ({...p, description: e.target.value}))} rows={2} placeholder="What this resource covers..." /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-bold">Category</Label>
                <Select value={form.category} onValueChange={v => setForm(p => ({...p, category: v}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c} className="capitalize">{c.replace('_', ' ')}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs font-bold">Type</Label>
                <Select value={form.type} onValueChange={v => setForm(p => ({...p, type: v}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="video">Video</SelectItem>
                    <SelectItem value="document">Document</SelectItem>
                    <SelectItem value="link">Link</SelectItem>
                    <SelectItem value="course">Course</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="text-xs font-bold">URL / Link *</Label><Input value={form.url} onChange={e => setForm(p => ({...p, url: e.target.value}))} placeholder="https://youtube.com/... or Google Drive link" data-testid="res-url" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-bold">Thumbnail URL (optional)</Label><Input value={form.thumbnail} onChange={e => setForm(p => ({...p, thumbnail: e.target.value}))} placeholder="Image URL for preview" /></div>
              <div><Label className="text-xs font-bold">Duration</Label><Input value={form.duration} onChange={e => setForm(p => ({...p, duration: e.target.value}))} placeholder="5 min, 1 hour, etc." /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Cancel</Button>
            <Button className="bg-[#4a9ac5] hover:bg-[#3d87b3] text-white" onClick={handleSave} disabled={!form.title?.trim()} data-testid="res-save-btn">{editRes ? 'Update' : 'Add Resource'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
